import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { PopoverController, ModalController, AlertController, LoadingController } from '@ionic/angular/lazy';
import { OrderService } from 'src/app/services/order.service';
import { Printer } from 'capacitor-printer';
import printJS from 'print-js';
import { StorageService } from 'src/app/services/storage.service';
import { DesktopPrintClientService } from 'src/app/services/desktop-print-client.service';
import { PermissionService } from 'src/app/services/permission.service';
import { ReceiptPrinterService } from 'src/app/services/receipt-printer.service';

@Component({
    selector: 'app-order-confirmation-print-options',
    templateUrl: './order-confirmation-print-options.component.html',
    styleUrls: ['./order-confirmation-print-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class OrderConfirmationPrintOptionsComponent implements OnInit {

    spinner = null;
    @Input() order_confirmation_page = null;
    order = null;
    order_has_tickets = false;
    order_has_gift_vouchers = false;
    printer_paper_sizes = [];
    receipt_printer = null;
    on_device = false;
    desktop_print_client_enabled = false;

    constructor(
        private popoverCtrl: PopoverController,
        public modalController: ModalController,
        public alertController: AlertController,
        public loadingController: LoadingController,
        private orderService: OrderService,
        private storageService: StorageService,
        private desktopPrintClientService: DesktopPrintClientService,
        public permissionService: PermissionService,
        private receiptPrinterService: ReceiptPrinterService
    ) { }

    private printReceiptImage(base64Image: any, options: { cutReceipt: boolean; openCashDrawer: boolean; width: number }): Promise<any> {
        return this.receiptPrinterService.printImage(base64Image, options).then((result) => {
            if(result !== 'printed') throw new Error(result);
        });
    }

    ngOnInit() {


        if(this.order_confirmation_page.order) {
            this.order = this.order_confirmation_page.order;
        }

        if(this.order_confirmation_page.order_has_tickets) {
            this.order_has_tickets = this.order_confirmation_page.order_has_tickets;
        }

        if(this.order_confirmation_page.order_has_gift_vouchers) {
            this.order_has_gift_vouchers = this.order_confirmation_page.order_has_gift_vouchers;
        }

        if(this.order_confirmation_page.on_device) {
            this.on_device = this.order_confirmation_page.on_device;
        }

        if(this.order_confirmation_page.printer_paper_sizes) {
            this.printer_paper_sizes = this.order_confirmation_page.printer_paper_sizes;
        }

        if(this.order_confirmation_page.receipt_printer) {
            this.receipt_printer = this.order_confirmation_page.receipt_printer;
        }

        this.storageService.get("printer_paper_sizes").then((printer_paper_sizes) => {
            if(printer_paper_sizes) {
                this.printer_paper_sizes = printer_paper_sizes;
            }
        });

        this.storageService.get("desktop_print_client_enabled").then((desktop_print_client_enabled) => {
            if(desktop_print_client_enabled) {
                this.desktop_print_client_enabled = desktop_print_client_enabled;
            }
        });
        
    }

    async printInvoice(printer_paper_size) {

        let format = "";

        if(this.on_device) {
            if(this.receipt_printer) {
                format = 'png_base64';
            }
            else {
                format = 'pdf_base64';
            }
        }
        else {
            format = 'pdf_base64';
        }

        let printing_dpc = false;

        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }

        this.presentSpinner("Printing invoice / receipt").then(() => {

            this.orderService.getOrderInvoice(this.order.id, format, printer_paper_size).then(async (invoice_content: any) => {

                if(invoice_content) {

                    if(this.on_device) {

                        if(this.receipt_printer) {

                            this.printReceiptImage(invoice_content, { cutReceipt: true, openCashDrawer: false, width: 576 }).then((result: any) => {

                            })
                            .catch((error: any) => {
                                this.presentAlert("Please check your printer settings", "");
                            });

                        }
                        else {            

                            Printer.printBase64({
                                name: 'Invoice / Receipt',
                                data: invoice_content,
                                mimeType: 'application/pdf' 
                            });

                        }
                    }
                    else {
                        if(printing_dpc) {
                            await this.desktopPrintClientService.printDocument({
                                document_type: 'invoice',
                                document_name: `Invoice #${this.order.number}`,
                                printer_type: printer_paper_size,
                                file_extension: 'pdf',
                                content: invoice_content
                            });
                        }
                        else {
                            printJS({ 
                                printable: invoice_content, 
                                type: 'pdf', 
                                base64: true 
                            });
                        }
                    }
                }
            })
            .catch(() => {

            })
            .finally(() => {
                this.spinner.dismiss();
                this.dismiss();
            });
        });

    }

    async printTickets(printer_paper_size) {

        let format = "";

        if(this.on_device) {
            if(this.receipt_printer) {
                format = 'png_base64';
            }
            else {
                format = 'pdf_base64';
            }
        }
        else {
            format = 'pdf_base64';
        }

        let printing_dpc = false;

        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }
        
        this.presentSpinner("Printing tickets").then(() => {
            
            if(!this.receipt_printer) {

                this.orderService.getOrderTickets(this.order.id, format, printer_paper_size).then(async (tickets_content: any) => {
                    
                    if(tickets_content) {

                        if(this.on_device) {
                            Printer.printBase64({
                                name: 'Tickets',
                                data: tickets_content,
                                mimeType: 'application/pdf' 
                            });
                        }
                        else {
                            
                            if(printing_dpc) {
                                await this.desktopPrintClientService.printDocument({
                                    document_type: 'ticket',
                                    document_name: `Tickets #${this.order.number}`,
                                    printer_type: printer_paper_size,
                                    file_extension: 'pdf',
                                    content: tickets_content
                                });
                            }
                            else {
                                printJS({ 
                                    printable: tickets_content, 
                                    type: 'pdf', 
                                    base64: true 
                                });
                            }
                        }
                    }
                })
                .catch(() => {

                })                
                .finally(() => {
                    this.spinner.dismiss();
                    this.dismiss();
                });

            }
            else {

                this.orderService.getOrderTickets(this.order.id, 'json', printer_paper_size).then(async (tickets: []) => {
                   
                    for (let i = 0; i < tickets.length; i++) {

                        this.orderService.getOrderTicket(tickets[i]['id'], 'png_base64', printer_paper_size).then(async (ticket_content: any) => {
                            await this.printReceiptImage(ticket_content, { cutReceipt: true, openCashDrawer: false, width: 576 }).then((result: any) => {

                            })
                            .catch((error: any) => {
                                // TODO: if first ticket fails, then whole loop should stop
                                this.presentAlert("Please check your printer settings", "");
                            });
                        });

                    }

                })
                .catch(() => {

                })
                .finally(() => {
                    this.spinner.dismiss();
                    this.dismiss();
                });
            }
        });
    }

    async printGiftVouchers(printer_paper_size) {

        let format = "";

        if(this.on_device) {
            if(this.receipt_printer) {
                format = 'png_base64';
            }
            else {
                format = 'pdf_base64';
            }
        }
        else {
            format = 'pdf_base64';
        }

        let printing_dpc = false;

        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }

        this.presentSpinner("Printing gift vouchers").then(() => {

            if(!this.receipt_printer) {

                this.orderService.getOrderGiftVouchers(this.order.id, format, printer_paper_size).then(async (gift_vouchers_content: any) => {

                    if(gift_vouchers_content) {

                        if(this.on_device) {
                            Printer.printBase64({
                                name: 'Gift vouchers',
                                data: gift_vouchers_content,
                                mimeType: 'application/pdf'
                            });
                        }
                        else {

                            if(printing_dpc) {
                                await this.desktopPrintClientService.printDocument({
                                    document_type: 'gift-voucher',
                                    document_name: `Gift vouchers #${this.order.number}`,
                                    printer_type: printer_paper_size,
                                    file_extension: 'pdf',
                                    content: gift_vouchers_content
                                });
                            }
                            else {
                                printJS({
                                    printable: gift_vouchers_content,
                                    type: 'pdf',
                                    base64: true
                                });
                            }
                        }
                    }
                })
                .catch(() => {

                })
                .finally(() => {
                    this.spinner.dismiss();
                    this.dismiss();
                });

            }
            else {

                this.orderService.getOrderGiftVouchers(this.order.id, 'json', printer_paper_size).then(async (gift_vouchers: []) => {

                    for (let i = 0; i < gift_vouchers.length; i++) {

                        this.orderService.getOrderGiftVoucher(gift_vouchers[i]['id'], 'png_base64', printer_paper_size).then(async (gift_voucher_content: any) => {
                            await this.printReceiptImage(gift_voucher_content, { cutReceipt: true, openCashDrawer: false, width: 576 }).then((result: any) => {

                            })
                            .catch((error: any) => {
                                // TODO: if first gift voucher fails, then whole loop should stop
                                this.presentAlert("Please check your printer settings", "");
                            });
                        });

                    }

                })
                .catch(() => {

                })
                .finally(() => {
                    this.spinner.dismiss();
                    this.dismiss();
                });
            }
        });
    }

    // NOTE: always A4
    async printXeroInvoice() {

        let printing_dpc = false;
        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }

        this.presentSpinner('Printing Xero invoice').then(() => {
            this.orderService.getOrderXeroInvoice(this.order.id).then(async (xero_invoice_content: any) => {
                if(xero_invoice_content) {
                    if(this.on_device) {
                        // NOTE: currently unsupported on capacitor
                    }
                    else {
                        if(printing_dpc) {
                            await this.desktopPrintClientService.printDocument({
                                document_type: 'xero-invoice',
                                document_name: `Xero invoice #${this.order.number}`,
                                printer_type: "a4",
                                file_extension: 'pdf',
                                content: xero_invoice_content
                            });
                        }
                        else {
                            printJS({ 
                                printable: xero_invoice_content,
                                type: 'pdf',
                                base64: true 
                            });
                        }
                    }
                }
            })
            .catch(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
            .finally(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
        });
    }

    // NOTE: always A4
    async printPackingSlip() {

        let format = "pdf_base64";

        let printing_dpc = false;
        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }

        this.presentSpinner('Printing packing slip').then(() => {
            this.orderService.getOrderPackingSlip(this.order.id, format).then(async (packing_slip_content: any) => {
                if(packing_slip_content) {
                    if(this.on_device) {
                        Printer.printBase64({
                            name: 'Packing slip',
                            data: packing_slip_content,
                            mimeType: 'application/pdf' 
                        });                        
                    }
                    else {
                        if(printing_dpc) {
                            await this.desktopPrintClientService.printDocument({
                                document_type: 'packing-slip',
                                document_name: `Packing slip #${this.order.number}`,
                                printer_type: "a4",
                                file_extension: 'pdf',
                                content: packing_slip_content
                            });
                        }
                        else {
                            printJS({ 
                                printable: packing_slip_content, 
                                type: 'pdf', 
                                base64: true 
                            });
                        }
                    }
                }
            })
            .catch(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
            .finally(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
        });
    }

    // NOTE: always A4
    async printDeliveryNote() {

        let format = "pdf_base64";

        let printing_dpc = false;
        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }

        this.presentSpinner('Printing delivery note').then(() => {
            this.orderService.getOrderDeliveryNote(this.order.id, format).then(async (delivery_note_content: any) => {
                if(delivery_note_content) {
                    if(this.on_device) {
                        Printer.printBase64({
                            name: 'Delivery note',
                            data: delivery_note_content,
                            mimeType: 'application/pdf' 
                        });                        
                    }
                    else {
                        if(printing_dpc) {
                            await this.desktopPrintClientService.printDocument({
                                document_type: 'delivery-note',
                                document_name: `Delivery note #${this.order.number}`,
                                printer_type: "a4",
                                file_extension: 'pdf',
                                content: delivery_note_content
                            });
                        }
                        else {
                            printJS({ 
                                printable: delivery_note_content, 
                                type: 'pdf', 
                                base64: true 
                            });
                        }
                    }
                }
            })
            .catch(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
            .finally(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
        });
    }

    // NOTE: always A4
    async printCollectionNote() {

        let format = "pdf_base64";

        let printing_dpc = false;
        if(!this.on_device && this.desktop_print_client_enabled) {
            if(this.desktopPrintClientService.ws_ready) {
                printing_dpc = true;
            }
            else {
                const connect_result = await this.desktopPrintClientService.connectPresent();
                if(connect_result === 'cancel') return;
                if(connect_result === 'connected') printing_dpc = true;
            }
        }

        this.presentSpinner('Printing collection note').then(async () => {
            this.orderService.getOrderCollectionNote(this.order.id, format).then(async (collection_note_content: any) => {
                if(collection_note_content) {
                    if(this.on_device) {
                        Printer.printBase64({
                            name: 'Collection note',
                            data: collection_note_content,
                            mimeType: 'application/pdf' 
                        });
                    }
                    else {
                        if(printing_dpc) {
                            await this.desktopPrintClientService.printDocument({
                                document_type: 'collection-note',
                                document_name: `Collection note #${this.order.number}`,
                                printer_type: "a4",
                                file_extension: 'pdf',
                                content: collection_note_content
                            });
                        }
                        else {
                            printJS({ 
                                printable: collection_note_content, 
                                type: 'pdf', 
                                base64: true 
                            });
                        }
                    }
                }
                this.spinner.dismiss();
                this.dismiss();
            })
            .catch(() => {
                this.spinner.dismiss();
                this.dismiss();
            })
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async dismiss() {
        try {
            await this.popoverCtrl.dismiss();
        } catch (e) {
            // click more than one time popover throws error, so ignore...
        }
    }

}
